<template>
  <div class="tw-record-page">
    <div class="page-header">
      <h2 class="page-title">图文发布记录</h2>
      <p class="page-desc">查看图文发布历史记录，支持重试失败任务</p>
    </div>

    <!-- 搜索区域 -->
    <div class="search-bar">
      <el-input v-model="searchForm.title" placeholder="搜索图文标题" clearable size="large"
        style="width: 300px; margin-right: 15px" @keyup.enter="handleSearch">
        <template #prefix>
          <el-icon>
            <Search />
          </el-icon>
        </template>
      </el-input>

      <!-- 添加账号下拉框 -->
      <el-select v-model="searchForm.video_account_id" placeholder="选择账号" clearable size="large"
        style="width: 180px; margin-right: 15px">
        <el-option label="不选择" :value="null" />
        <el-option v-for="item in videoAccountOptions" :key="item.id" :label="item.account_name" :value="item.id" />
      </el-select>

      <el-button type="primary" size="large" @click="handleSearch">
        <el-icon>
          <Search />
        </el-icon>
        搜索
      </el-button>

      <el-button type="info" size="large" @click="resetSearch" style="margin-left: 10px">
        <el-icon>
          <Refresh />
        </el-icon>
        重置
      </el-button>
    </div>

    <!-- 记录列表 -->
    <div class="table-wrapper">
      <el-table :data="recordList" stripe v-loading="loading" class="record-table">
        <el-table-column label="账号" prop="account_name" width="180" />
        <el-table-column label="图文标题" min-width="220" show-overflow-tooltip>
          <template #default="{ row }">
            <span :class="{ 'empty-title': !row.work_description && !row.title && !row.topic }">
              {{ row.work_description || row.title || row.topic || '无标题' }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="任务名称" prop="task_name" width="150" />

        <!-- 错误提示列 -->
        <el-table-column label="错误提示" width="180">
          <template #default="{ row }">
            <el-popover v-if="row.error_message" placement="top-start" width="300" trigger="hover">
              <template #reference>
                <el-tag type="danger" size="small" style="cursor: pointer">
                  <el-icon>
                    <Warning />
                  </el-icon>
                  查看错误
                </el-tag>
              </template>
              <div class="error-message-content">
                {{ row.error_message }}
              </div>
            </el-popover>
            <span v-else class="empty-value">无错误</span>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="120">
          <template #default="{ row }">
            <el-tag :type="getStatusTagType(row.status)" size="small">
              {{ getStatusText(row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="草稿时间" prop="drafted_at" width="180" />
        <el-table-column label="发布时间" prop="published_at" width="180" />
        <el-table-column label="备注" prop="notes" width="180" show-overflow-tooltip />

        <el-table-column label="重试" width="100" fixed="right">
          <template #default="{ row }">
            <el-button type="warning" size="small" :disabled="[1, 2, 4, 5].includes(row.status)"
              @click="handleRetry(row)">
              <el-icon>
                <Refresh />
              </el-icon>
              重试
            </el-button>
          </template>
        </el-table-column>

        <el-table-column label="操作" width="120" fixed="right">
          <template #default="scope">
            <el-button type="danger" size="small" @click="handleDelete(scope.row.id)"
              :disabled="scope.row.status === 2">
              <el-icon>
                <Delete />
              </el-icon>
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 分页组件 -->
    <div class="pagination-container">
      <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.pageSize"
        :total="pagination.total" :page-sizes="[10, 20, 30, 50]" layout="total, sizes, prev, pager, next, jumper"
        @size-change="fetchRecordList" @current-change="fetchRecordList" />
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import config from '@/config.js';
import Cookies from 'js-cookie';
import { Search, Refresh, Delete, Warning } from '@element-plus/icons-vue';

export default {
  name: 'TwRecord',
  components: { Search, Refresh, Delete, Warning },
  data() {
    return {
      loading: false,
      searchForm: {
        title: '',
        video_account_id: null
      },
      recordList: [],
      videoAccountOptions: [],
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      }
    }
  },
  computed: {
    userInfo() {
      return JSON.parse(Cookies.get('user_info') || '{}')
    }
  },
  async mounted() {
    await this.fetchVideoAccountOptions();
    await this.fetchRecordList();
  },
  methods: {
    async handleRetry(row) {
      // 前端先检查不允许重试的状态
      const disallowedStatuses = [1, 2, 4, 5];
      if (disallowedStatuses.includes(row.status)) {
        this.$message.warning(`当前状态为"${this.getStatusText(row.status)}"，不能重试`);
        return;
      }

      try {
        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/tw_record_management.php`,
          {
            action: 'retry',
            id: row.id,
            store_id: this.userInfo.id
          }
        );

        if (data.success) {
          this.$message.success('任务已重置为待发送状态');
          this.fetchRecordList();
        } else {
          this.$message.error(data.error || '重试任务失败');
        }
      } catch (error) {
        const errorMsg = error.response?.data?.error || error.message || '重试任务失败';
        this.$message.error(`重试失败: ${errorMsg}`);
        console.error('重试错误:', error);
      }
    },

    // 获取账号选项
    async fetchVideoAccountOptions() {
      try {
        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/video_account_api.php`,
          {
            action: 'list_all',
            store_id: this.userInfo.id
          }
        );

        if (data.data) {
          this.videoAccountOptions = data.data;
        }
      } catch (error) {
        console.error('获取账号列表失败:', error);
      }
    },

    // 获取账号名称
    getAccountName(id) {
      const account = this.videoAccountOptions.find(g => g.id == id);
      return account ? account.account_name : `账号${id}`;
    },

    // 获取记录列表
    async fetchRecordList() {
      this.loading = true;
      try {
        const params = {
          action: 'search',
          store_id: this.userInfo.id,
          page: this.pagination.page,
          page_size: this.pagination.pageSize
        };

        if (this.searchForm.title) {
          params.title = this.searchForm.title;
        }

        if (this.searchForm.video_account_id) {
          params.video_account_id = this.searchForm.video_account_id;
        }

        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/tw_record_management.php`,
          params
        );

        if (data.data) {
          this.recordList = data.data.map(item => ({
            ...item,
            account_name: this.getAccountName(item.video_account_id)
          }));
          this.pagination.total = data.pagination?.total || data.data.length;
        }
      } catch (error) {
        this.$message.error('获取记录失败');
        console.error('获取记录错误:', error);
      } finally {
        this.loading = false;
      }
    },

    // 获取状态文本
    getStatusText(status) {
      const statusMap = {
        1: '待发送',
        2: '发送成功',
        3: '发送失败',
        4: '发布处理中',
        5: '已提交发布'
      };
      return statusMap[status] || `状态${status}`;
    },

    // 获取状态标签类型
    getStatusTagType(status) {
      const typeMap = {
        1: 'warning',
        2: 'success',
        3: 'danger',
        4: 'info',
        5: 'primary'
      };
      return typeMap[status] || 'info';
    },

    // 处理搜索
    handleSearch() {
      this.pagination.page = 1;
      this.fetchRecordList();
    },

    // 重置搜索
    resetSearch() {
      this.searchForm = {
        title: '',
        video_account_id: null
      };
      this.pagination.page = 1;
      this.fetchRecordList();
    },

    // 删除记录
    async handleDelete(id) {
      this.$confirm('确定要删除此记录吗？', '警告', {
        confirmButtonText: '确认删除',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const { data } = await axios.post(
            `${config.BASE_URL}/shop/api/tw_record_management.php`,
            {
              action: 'delete',
              id: id,
              store_id: this.userInfo.id
            }
          );

          if (data.success) {
            this.$message.success('删除成功');
            this.fetchRecordList();
          } else {
            this.$message.error(data.error || '删除失败');
          }
        } catch (error) {
          const errorMsg = error.response?.data?.error || error.message || '删除失败';
          this.$message.error(`删除失败: ${errorMsg}`);
          console.error('删除错误:', error);
        }
      }).catch(() => { });
    }
  }
}
</script>

<style scoped lang="scss">
.tw-record-page {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow: hidden;

  .page-header {
    margin-bottom: 24px;

    .page-title {
      font-size: 24px;
      font-weight: 600;
      color: #1a1a2e;
      margin-bottom: 4px;
    }

    .page-desc {
      font-size: 14px;
      color: #8c8c8c;
    }
  }
}

.page-title {
  font-size: 24px;
  color: #1f2937;
  margin-bottom: 32px;
  font-weight: 600;
  text-align: center;
  letter-spacing: 0.5px;
}

.search-bar {
  display: flex;
  align-items: center;
  margin-bottom: 24px;
  padding: 16px;
  background: #f8fafc;
  border-radius: 8px;
  flex-wrap: wrap;
  gap: 10px;
}

.pagination-container {
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
}

.table-wrapper {
  position: relative;
  width: 100%;
  overflow-x: auto;
  margin-top: 20px;
  -webkit-overflow-scrolling: touch;
}

.record-table {
  :deep(.el-table__header) th {
    background-color: #f5f7fa;
    color: #606266;
    font-weight: 600;
    white-space: nowrap;
  }

  :deep(.el-table__body) tr:hover>td {
    background-color: #f0f7ff !important;
  }
  
  :deep(.el-table__body-wrapper) {
    overflow-x: auto !important;
    
    &::-webkit-scrollbar {
      height: 8px;
    }

    &::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: #c1c1c1;
      border-radius: 4px;

      &:hover {
        background: #a8a8a8;
      }
    }
  }
  
  :deep(.el-table__header-wrapper) {
    overflow: hidden !important;
    width: 100% !important;
  }
}

/* 操作按钮样式 */
.el-button {
  display: inline-flex;
  align-items: center;

  .el-icon {
    margin-right: 5px;
  }
}

/* 错误提示样式 */
.error-message-content {
  max-height: 200px;
  overflow-y: auto;
  word-break: break-word;
  white-space: pre-wrap;
  padding: 8px;
  font-size: 13px;
  line-height: 1.5;
}

.empty-title {
  color: #909399;
  font-style: italic;
}

.empty-value {
  color: #909399;
  font-style: italic;
  font-size: 12px;
}

/* 响应式优化 */
@media (max-width: 768px) {
  .search-bar {
    flex-direction: column;
    align-items: stretch;
    
    .el-input {
      width: 100% !important;
      margin-right: 0 !important;
    }
    
    .el-select {
      width: 100% !important;
      margin-right: 0 !important;
    }
  }
  
  .record-table {
    font-size: 12px;
  }
}
</style>